/**
 * Tipos e formatação compartilhados pelos blocos do dashboard.
 *
 * Extraídos do HomeDashboard quando ele virou uma lista de blocos renderizada a
 * partir de um registro (ver ../painel/blocos.ts). Ficam num arquivo só porque
 * TODO bloco precisa deles e importar de dentro do HomeDashboard criaria ciclo:
 * o HomeDashboard importa os blocos.
 */

export interface PendingSale {
  id: string;
  nome_consorciado: string;
  contrato: string | null;
  group_number: number | null;
  valor_parcela: number | null;
  valor_credito: number | null;
  status: string;
  created_at: string;
}

export interface InadimplenteRow {
  id: number;
  consorciado: string;
  vendedor_nome: string;
  /**
   * Parcelas vencidas e não pagas. Já se chamou `dias_atraso` — nome errado
   * herdado do parser, corrigido na migration 165. Ver `parcelasEmAberto.ts`.
   */
  parcelas_em_aberto: number;
  valor_atraso: number;
  vendedor_cpf_cnpj: string;
}

/**
 * Uma PESSOA, não um contrato.
 *
 * Os dois blocos de lista mostravam cinco registros crus, e um cliente com
 * cinco contratos em atraso ocupava o card inteiro repetindo o próprio nome
 * cinco vezes — cinco linhas pra dizer uma coisa só, e os outros clientes em
 * atraso invisíveis. Agrupado, a mesma altura de card mostra cinco clientes
 * diferentes e o total de cada um.
 *
 * O agrupamento acontece sobre a lista INTEIRA antes de cortar no top 5;
 * agrupar depois só mascararia a repetição sem recuperar o espaço.
 */
export interface InadimplenteAgrupado {
  id: number;
  consorciado: string;
  vendedor_nome: string;
  /** O contrato mais atrasado da pessoa, em parcelas em aberto. */
  parcelas_em_aberto: number;
  /** A soma do que ela deve. */
  valor_atraso: number;
  contratos: number;
}

export interface PendenteAgrupado {
  id: string;
  nome_consorciado: string;
  /** Grupo, quando as cotas são todas do mesmo. Null quando divergem. */
  group_number: number | null;
  contrato: string | null;
  /** Soma das parcelas das cotas pendentes desta pessoa. */
  valor_parcela: number;
  cotas: number;
}

export interface DashboardData {
  vendasAtivasCount: number;
  vendasAtivasValor: number;
  vendasAtivasOwnCount: number;
  vendasAnoValor: number;
  vendasAnoCount: number;
  vendasMesValor: number;
  vendasMesCount: number;
  inadCount: number;
  inadValor: number;
  inadOwnCount: number;
  propostasCount: number;
  propostasOwnCount: number;
  comissoesMes: number;
  pendingCount: number;
  pendingValor: number;
  /** `chave` é 'YYYY-MM' — é o que permite clicar na barra e cair no mês certo. */
  monthly: { label: string; count: number; valor: number; chave: string }[];
  topInadimplentes: InadimplenteAgrupado[];
  topPendentes: PendenteAgrupado[];
}

/**
 * O que todo bloco recebe.
 *
 * O escopo vem POR PROP, não por hook. `useRoleDataScope` não é
 * contextualizado — cada consumidor refaz duas chamadas RPC — e quebrar a
 * página em oito blocos que o chamassem multiplicaria isso por oito. O
 * HomeDashboard resolve uma vez e distribui.
 */
export type BlocoCtx = {
  isSuperAdmin: boolean;
  isManager: boolean;
  currentCrmUserId: number | null;
  /** Super admin olhando como outra pessoa. Null = todos. */
  viewAsCrmUserId: number | null;
  navigate: (to: string) => void;
  mesLabel: string;
  anoLabel: string;
  mesRange: { from: string; to: string };
  anoRange: { from: string; to: string };
  /** "N de usuários gerenciados", ou undefined quando não faz sentido mostrar. */
  managedNote: (total: number, own: number) => string | undefined;
};

/**
 * `motivo` é o destaque do agente — por que ESTE bloco importa hoje. Cada bloco
 * repassa pro seu `BlocoCard`, que o desenha no header. Chega por prop em vez de
 * por invólucro justamente porque um bloco que se esconde sozinho tem que levar
 * o motivo embora junto.
 */
export type BlocoProps = { data: DashboardData; ctx: BlocoCtx; motivo?: string };

/**
 * `compact` = sem centavos (valor cheio em reais inteiros). NÃO usa notação
 * compacta "mi" — ela arredondava 1.300.000 pra "R$ 1 mi" e confundia (parecia
 * que faltava produção). Mostra sempre o valor inteiro: "R$ 1.300.000".
 */
export const formatCurrency = (value: number, compact = false) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  }).format(value || 0);
